import { inject, Injectable } from '@angular/core';
import { CookieConsentService } from '@core/services/cookie-consent.service';
import { EnvironmentService } from '@core/services/environment.service';
import { Tracker } from '@core/services/event-tracker.service';
import { IdleCallbackService } from '@core/services/idle-callback.service';
import { PlausibleService } from '@core/services/plausible.service';
import { WINDOW } from '@core/services/window.service';
import {
    ANALYTICS_EVENTS,
    AnalyticsEventName,
} from '@core/utils/AnalyticsEvents';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ScriptLoaderService, ScriptModel } from './script-loader.service';

interface CE2 {
    converted: (trackingId) => unknown;
    startRecording: () => unknown;
    stopRecording: () => unknown;
}

type CrazyEggWindow = Window & {
    CE_API: any[];
    CE2: CE2;
};

const trackingIdMapping = {
    [ANALYTICS_EVENTS.TRIP_ENQUIRY_SENT]:
        'c7666b4b-5102-42f3-82f6-03b151d834de',
};

@Injectable({
    providedIn: 'root',
})
export class CrazyEggService implements Tracker {
    private cookieConsentService = inject(CookieConsentService);
    private environmentService = inject(EnvironmentService);
    private plausibleService = inject(PlausibleService);
    private idleCallbackService = inject(IdleCallbackService);
    private scriptLoaderService = inject(ScriptLoaderService);
    private window = inject<CrazyEggWindow>(WINDOW);
    private scriptLoaded: Observable<ScriptModel>;

    setup() {
        if (this.environmentService.isBrowser()) {
            this.scriptLoaded = this.cookieConsentService.consentChanged.pipe(
                map(
                    consentGiven =>
                        consentGiven && this.plausibleService.isActive()
                ),
                switchMap(loadScript =>
                    loadScript
                        ? this.idleCallbackService
                              .firstIdleCallback()
                              .pipe(switchMap(() => this.loadCrazyEgg()))
                        : of(null)
                ),
                shareReplay(1)
            );
            this.scriptLoaded.subscribe();
        } else {
            this.scriptLoaded = of(null);
        }
    }

    private loadCrazyEgg() {
        return this.scriptLoaderService.load(
            '//script.crazyegg.com/pages/scripts/0118/4030.js'
        );
    }

    private getCE2(): Observable<null | CE2> {
        if (this.environmentService.isBrowser()) {
            return this.scriptLoaded.pipe(
                switchMap(() => this.cookieConsentService.consentChanged),
                take(1),
                map(consentGiven => {
                    if (consentGiven && this.plausibleService.isActive()) {
                        return this.window.CE2;
                    }
                })
            );
        }
        return of(null);
    }

    trackEvent(
        name: AnalyticsEventName,
        props?: { [key: string]: string | number | boolean }
    ): void {
        const trackingId = trackingIdMapping[name];

        if (trackingId) {
            this.getCE2().subscribe(CE2 => {
                CE2?.converted(trackingId);
            });
        }
    }

    startRecording() {
        /* Will start recording the user's interactions on the page,
           safe for ssr and considers the users tracking preferences */
        this.getCE2().subscribe(CE2 => {
            CE2?.startRecording();
        });
    }

    stopRecording() {
        this.getCE2().subscribe(CE2 => {
            CE2?.stopRecording();
        });
    }
}
